import { useMutation, useSuspenseQuery } from '@apollo/client';
import { skipToken } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';

import { useApiClient } from '@eluve/api-client-provider';
import {
  Badge,
  Button,
  DateTimePicker,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  Form,
  FormField,
  FormItem,
  FormMessage,
  Grid,
  Heading,
  Icon,
  Input,
  InputLabel,
  Label,
  LoadingIcon,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
  Stack,
  Text,
  Textarea,
  useToast,
} from '@eluve/components';
import { formatDate } from '@eluve/date-utils';
import { DbEnums } from '@eluve/graphql-types';

import { DeleteExperimentAction } from './DeleteExperimentAction';
import { ExperimentStatusToggle } from './ExperimentStatusToggle';
import { ExperimentVariantsList } from './ExperimentVariantsList';
import { TestAssignmentSection } from './TestAssignmentSection';
import { TestingOverridesSection } from './TestingOverridesSection';
import {
  createExperimentMutation,
  getExperimentByIdQuery,
  getExperimentsQuery,
  updateExperimentMutation,
} from './operations';

const TIMEZONES = [
  { value: 'Pacific/Honolulu', label: 'UTC-10:00 Hawaii' },
  { value: 'America/Anchorage', label: 'UTC-09:00 Alaska' },
  { value: 'America/Los_Angeles', label: 'UTC-08:00 Pacific Time (US)' },
  { value: 'America/Denver', label: 'UTC-07:00 Mountain Time (US)' },
  { value: 'America/Chicago', label: 'UTC-06:00 Central Time (US)' },
  { value: 'America/New_York', label: 'UTC-05:00 Eastern Time (US)' },
  { value: 'America/Halifax', label: 'UTC-04:00 Atlantic Time' },
  { value: 'America/Sao_Paulo', label: 'UTC-03:00 São Paulo' },
  { value: 'Atlantic/South_Georgia', label: 'UTC-02:00 Mid-Atlantic' },
  { value: 'Atlantic/Azores', label: 'UTC-01:00 Azores' },
  { value: 'UTC', label: 'UTC+00:00 UTC' },
  { value: 'Europe/London', label: 'UTC+00:00 London' },
  { value: 'Europe/Paris', label: 'UTC+01:00 Paris, Berlin' },
  { value: 'Europe/Helsinki', label: 'UTC+02:00 Helsinki, Cairo' },
  { value: 'Europe/Moscow', label: 'UTC+03:00 Moscow' },
  { value: 'Asia/Dubai', label: 'UTC+04:00 Dubai' },
  { value: 'Asia/Karachi', label: 'UTC+05:00 Karachi' },
  { value: 'Asia/Dhaka', label: 'UTC+06:00 Dhaka' },
  { value: 'Asia/Bangkok', label: 'UTC+07:00 Bangkok' },
  { value: 'Asia/Jakarta', label: 'UTC+07:00 Jakarta' },
  { value: 'Asia/Singapore', label: 'UTC+08:00 Singapore, Perth' },
  { value: 'Asia/Tokyo', label: 'UTC+09:00 Tokyo, Seoul' },
  { value: 'Australia/Sydney', label: 'UTC+10:00 Sydney, Melbourne' },
  { value: 'Pacific/Auckland', label: 'UTC+12:00 Auckland' },
] as const;

const EXPERIMENT_MODES = [
  {
    value: 'AB_TEST',
    label: 'A/B Test',
    description: 'Selects one variant config for summary generation',
  },
  {
    value: 'COMPARISON',
    label: 'Comparison',
    description: 'Both variants generate summaries for side-by-side selection',
  },
] as const;

const experimentFormSchema = z.object({
  name: z.string().min(1, 'Experiment name is required'),
  description: z.string().optional(),
  posthogFlagKey: z.string().min(1, 'PostHog flag key is required'),
  mode: z
    .nativeEnum(DbEnums.ExperimentModeTypes)
    .default(DbEnums.ExperimentModeTypes.AB_TEST),
  timezone: z.string().optional(),
  startTime: z.date().optional(),
  endTime: z.date().optional(),
  rank: z.coerce.number().int().default(0),
});

type ExperimentFormValues = z.infer<typeof experimentFormSchema>;

type TestingOverride = {
  userId: string;
  variantKey: string;
};

type ExperimentData = {
  id: string;
  name: string;
  description: string | null;
  posthogFlagKey: string;
  timezone: string | null;
  startTime: string | null;
  endTime: string | null;
  isActive: boolean;
  firstActivatedAt: string | null;
  rank: number;
  mode: keyof typeof DbEnums.ExperimentModeTypes;
  testingOverrides: TestingOverride[] | null;
  createdAt: string;
  updatedAt: string;
  variants: Array<{
    id: string;
    experimentId: string;
    name: string;
    posthogVariantKey: string;
    variantConfig: Record<string, unknown>;
    createdAt: string;
    updatedAt: string;
  }>;
};

export const ExperimentPage: React.FC = () => {
  const { experimentId } = useParams<{ experimentId: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const apiClient = useApiClient();

  const isCreateMode = experimentId === 'new';

  const [flagValidation, setFlagValidation] = useState<{
    isValidating: boolean;
    exists: boolean | null;
  }>({ isValidating: false, exists: null });

  const { data } = useSuspenseQuery(
    getExperimentByIdQuery,
    isCreateMode || !experimentId ? skipToken : { variables: { experimentId } },
  );

  const experiment = data?.experimentsByPk as ExperimentData | null;
  const isActivated = experiment?.firstActivatedAt != null;

  const parseDate = (dateStr: string | null | undefined): Date | undefined => {
    if (!dateStr) return undefined;
    const date = new Date(dateStr);
    return isNaN(date.getTime()) ? undefined : date;
  };

  const form = useForm<ExperimentFormValues>({
    resolver: zodResolver(experimentFormSchema),
    values: isCreateMode
      ? {
          name: '',
          description: '',
          posthogFlagKey: '',
          mode: DbEnums.ExperimentModeTypes.AB_TEST,
          timezone: '',
          startTime: undefined,
          endTime: undefined,
          rank: 0,
        }
      : {
          name: experiment?.name ?? '',
          description: experiment?.description ?? '',
          posthogFlagKey: experiment?.posthogFlagKey ?? '',
          mode: experiment?.mode ?? DbEnums.ExperimentModeTypes.AB_TEST,
          timezone: experiment?.timezone ?? '',
          startTime: parseDate(experiment?.startTime),
          endTime: parseDate(experiment?.endTime),
          rank: experiment?.rank ?? 0,
        },
    mode: 'onChange',
  });

  const [createExperiment] = useMutation(createExperimentMutation, {
    refetchQueries: [{ query: getExperimentsQuery }],
    onCompleted: (data) => {
      const newId = (data?.insertExperimentsOne as { id: string })?.id;
      if (newId) {
        navigate(`/admin/experiments/${newId}`, { replace: true });
      }
    },
    onError: () => {
      toast.error('Failed to create experiment');
    },
  });

  const [updateExperiment] = useMutation(updateExperimentMutation, {
    onCompleted: () => {
      toast.success('Experiment updated');
      form.reset(form.getValues());
    },
    onError: () => {
      toast.error('Failed to update experiment');
    },
  });

  const onSubmit = (data: ExperimentFormValues) => {
    if (isCreateMode || !experimentId) {
      createExperiment({
        variables: {
          name: data.name,
          description: data.description || null,
          posthogFlagKey: data.posthogFlagKey,
          mode: data.mode ?? DbEnums.ExperimentModeTypes.AB_TEST,
          timezone: data.timezone || null,
          startTime: data.startTime?.toISOString() ?? null,
          endTime: data.endTime?.toISOString() ?? null,
          rank: data.rank,
        },
      });
    } else {
      updateExperiment({
        variables: {
          experimentId: experimentId,
          name: data.name,
          description: data.description || null,
          posthogFlagKey: data.posthogFlagKey,
          mode: data.mode ?? DbEnums.ExperimentModeTypes.AB_TEST,
          timezone: data.timezone || null,
          startTime: data.startTime?.toISOString() ?? null,
          endTime: data.endTime?.toISOString() ?? null,
          rank: data.rank,
        },
      });
    }
  };

  const handleCancel = () => {
    if (isCreateMode) {
      navigate('/admin/experiments');
    } else {
      form.reset();
    }
  };

  const handleFlagKeyBlur = async () => {
    const flagKey = form.getValues('posthogFlagKey');
    if (!flagKey || !isCreateMode) return;

    setFlagValidation({ isValidating: true, exists: null });

    try {
      const response = await apiClient.experiments.syncFromPostHog({
        body: { posthogFlagKey: flagKey },
      });

      if (response.status === 200) {
        setFlagValidation({ isValidating: false, exists: true });
      } else if (response.status === 404) {
        setFlagValidation({ isValidating: false, exists: false });
      } else {
        setFlagValidation({ isValidating: false, exists: null });
      }
    } catch {
      setFlagValidation({ isValidating: false, exists: null });
    }
  };

  return (
    <Stack gap={6}>
      <Stack gap={3} align="center" direction="horizontal">
        <Stack gap={3} align="center" direction="horizontal">
          <div className="my-2 py-2">
            <Heading>
              {isCreateMode ? 'New Experiment' : experiment?.name}
            </Heading>
          </div>
          {!isCreateMode && experiment && (
            <>
              <Badge variant={experiment.isActive ? 'success' : 'secondary'}>
                {experiment.isActive ? 'Active' : 'Inactive'}
              </Badge>
              {isActivated && <Badge variant="info">Locked</Badge>}
            </>
          )}
        </Stack>
        {!isCreateMode && experiment && (
          <Stack gap={2} fullWidth={false} direction="horizontal">
            <TestAssignmentSection experimentId={experiment.id} />
            <ExperimentStatusToggle experimentId={experiment.id} />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline">
                  <Icon name="MoreHorizontal" size="auto" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DeleteExperimentAction
                  experimentId={experiment.id}
                  experimentName={experiment.name}
                  isActivated={isActivated ?? false}
                  asMenuItem
                />
              </DropdownMenuContent>
            </DropdownMenu>
          </Stack>
        )}
      </Stack>

      {!isCreateMode && experiment && (
        <div className="w-full rounded-lg border border-border-primary bg-bg-gray-subtle p-4">
          <Stack gap={4}>
            <Grid columns={2} gap={4} className="md:grid-cols-5">
              <Stack gap={1}>
                <Text size="xs" color="tertiary">
                  First Activated
                </Text>
                <Text size="md">
                  {experiment.firstActivatedAt
                    ? formatDate.toLocalDateTime(experiment.firstActivatedAt)
                    : '-'}
                </Text>
              </Stack>
              <Stack gap={1}>
                <Text size="xs" color="tertiary">
                  Last Updated
                </Text>
                <Text size="md">
                  {formatDate.toLocalDateTime(experiment.updatedAt)}
                </Text>
              </Stack>
              <Stack gap={1}>
                <Text size="xs" color="tertiary">
                  Created At
                </Text>
                <Text size="md">
                  {formatDate.toLocalDateTime(experiment.createdAt)}
                </Text>
              </Stack>
              <Stack gap={1}>
                <Text size="xs" color="tertiary">
                  Mode
                </Text>
                <Text size="md">
                  {EXPERIMENT_MODES.find((m) => m.value === experiment.mode)
                    ?.label ?? experiment.mode}
                </Text>
              </Stack>
              <Stack gap={1}>
                <Text size="xs" color="tertiary">
                  Variants
                </Text>
                <Text size="md">{experiment.variants?.length ?? 0}</Text>
              </Stack>
            </Grid>
          </Stack>
        </div>
      )}

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="w-full">
          <Stack gap={6}>
            <Grid columns={1} gap={6} className="md:grid-cols-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field, fieldState: { error } }) => (
                  <FormItem className="w-full">
                    <Label className="w-full">
                      <Stack gap={2}>
                        Name *
                        <Input
                          placeholder="Experiment name"
                          className="w-full"
                          {...field}
                        />
                        {error && <FormMessage>{error.message}</FormMessage>}
                      </Stack>
                    </Label>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="posthogFlagKey"
                render={({ field, fieldState: { error } }) => (
                  <FormItem className="w-full">
                    <Label className="w-full">
                      <Stack gap={2}>
                        PostHog Flag Key *
                        <Stack gap={2} align="center" direction="horizontal">
                          <Input
                            placeholder="posthog-flag-key"
                            className="w-full"
                            autoComplete="off"
                            autoCorrect="off"
                            autoCapitalize="off"
                            spellCheck={false}
                            {...field}
                            disabled={isActivated ?? false}
                            onBlur={() => {
                              field.onBlur();
                              handleFlagKeyBlur();
                            }}
                          />
                          {isCreateMode && flagValidation.isValidating && (
                            <LoadingIcon
                              size="lg"
                              className="text-text-tertiary"
                            />
                          )}
                          {isCreateMode && flagValidation.exists === true && (
                            <Icon
                              name="CheckCircle"
                              size="lg"
                              className="text-text-positive"
                            />
                          )}
                        </Stack>
                        {error && <FormMessage>{error.message}</FormMessage>}
                        {isCreateMode && flagValidation.exists === false && (
                          <Text size="xs" color="tertiary">
                            Feature flag doesn't exist in PostHog, ensure it is
                            created
                          </Text>
                        )}
                        {isActivated && (
                          <Text size="xs" color="tertiary">
                            Cannot change after activation
                          </Text>
                        )}
                      </Stack>
                    </Label>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="mode"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <Label className="w-full">
                      <Stack gap={2}>
                        Mode
                        <Select
                          onValueChange={field.onChange}
                          value={field.value ?? 'AB_TEST'}
                          disabled={isActivated ?? false}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select mode" />
                          </SelectTrigger>
                          <SelectContent>
                            {EXPERIMENT_MODES.map((m) => (
                              <SelectItem key={m.value} value={m.value}>
                                {m.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Text size="xs" color="tertiary">
                          {
                            EXPERIMENT_MODES.find(
                              (m) => m.value === (field.value ?? 'AB_TEST'),
                            )?.description
                          }
                        </Text>
                        {isActivated && (
                          <Text size="xs" color="tertiary">
                            Cannot change after activation
                          </Text>
                        )}
                      </Stack>
                    </Label>
                  </FormItem>
                )}
              />
            </Grid>

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem className="w-full">
                  <Label className="w-full">
                    <Stack gap={2}>
                      Description
                      <Textarea
                        placeholder="Optional description of the experiment"
                        className="w-full"
                        rows={3}
                        {...field}
                        value={field.value ?? ''}
                      />
                    </Stack>
                  </Label>
                </FormItem>
              )}
            />

            <Grid columns={1} gap={6} className="md:grid-cols-4">
              <FormField
                control={form.control}
                name="timezone"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <Label className="w-full">
                      <Stack gap={2}>
                        Timezone
                        <Select
                          onValueChange={field.onChange}
                          value={field.value ?? ''}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select timezone" />
                          </SelectTrigger>
                          <SelectContent>
                            {TIMEZONES.map((tz) => (
                              <SelectItem key={tz.value} value={tz.value}>
                                {tz.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </Stack>
                    </Label>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="startTime"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <InputLabel label="Start Time">
                      <DateTimePicker
                        dateTime={field.value}
                        onDateTimeChange={field.onChange}
                        placeholder="Select start time"
                      />
                    </InputLabel>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="endTime"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <InputLabel label="End Time">
                      <DateTimePicker
                        dateTime={field.value}
                        onDateTimeChange={field.onChange}
                        placeholder="Select end time"
                      />
                    </InputLabel>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="rank"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <Label className="w-full">
                      <Stack gap={2}>
                        Rank
                        <Input
                          type="number"
                          placeholder="0"
                          className="w-full"
                          {...field}
                          onChange={(e) =>
                            field.onChange(Number(e.target.value))
                          }
                        />
                        <Text size="xs" color="tertiary">
                          Higher = higher priority
                        </Text>
                      </Stack>
                    </Label>
                  </FormItem>
                )}
              />
            </Grid>

            <Stack justify="end" gap={2} direction="horizontal">
              <Button
                variant="outline"
                onClick={handleCancel}
                disabled={!form.formState.isDirty}
              >
                {isCreateMode ? 'Cancel' : 'Cancel Edit'}
              </Button>
              <Button
                disabled={
                  form.formState.isSubmitting ||
                  !form.formState.isValid ||
                  !form.formState.isDirty ||
                  (isCreateMode && flagValidation.exists !== true)
                }
                type="submit"
              >
                {isCreateMode ? 'Create Experiment' : 'Save Changes'}
              </Button>
            </Stack>
          </Stack>
        </form>
      </Form>

      {!isCreateMode && experiment && (
        <>
          <Separator className="my-2" />
          <ExperimentVariantsList
            experimentId={experiment.id}
            variants={experiment.variants ?? []}
            isActivated={isActivated ?? false}
            posthogFlagKey={experiment.posthogFlagKey ?? ''}
          />
        </>
      )}

      {!isCreateMode && experiment && (
        <>
          <Separator className="my-2" />
          <TestingOverridesSection
            experimentId={experiment.id}
            variants={experiment.variants ?? []}
            testingOverrides={
              (experiment.testingOverrides ?? []) as TestingOverride[]
            }
          />
        </>
      )}
    </Stack>
  );
};
